import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { Monitor, Wrench, Search, RefreshCw, Wifi, WifiOff, Cpu, Globe2, Shield, Building2, Trash2, Rocket } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RemoteControlDialog } from "@/components/app/RemoteControlDialog";
import { toast } from "sonner";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

/**
 * Admin cross-tenant fleet view. Lists every paired screen across every
 * customer so support/ops can troubleshoot without impersonating accounts.
 * Reachable from the sidebar for admin users only.
 */
export default function AdminFleet() {
  const { user } = useAuth();
  const [screens, setScreens] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");     // all | online | offline | unpaired
  const [versionFilter, setVersionFilter] = useState("all");   // all | "1.4.3" | "1.4.2" | …
  const [remoteScreen, setRemoteScreen] = useState(null);
  const [deletingId, setDeletingId] = useState(null);          // guards double-clicks + shows spinner
  const [broadcasting, setBroadcasting] = useState(false);     // Force update all sticks in-flight

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: fleet }, { data: st }] = await Promise.all([
        api.get("/admin/fleet/screens", {
          params: {
            q: q || undefined,
            status: statusFilter === "all" ? undefined : statusFilter,
            version: versionFilter === "all" ? undefined : versionFilter,
            limit: 500,
          },
        }),
        api.get("/admin/fleet/stats"),
      ]);
      setScreens(fleet.screens || []);
      setStats(st);
    } catch (err) {
      toast.error("Failed to load fleet");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, versionFilter]);

  // Debounce search so typing doesn't hammer the backend
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const h = setTimeout(load, 300);
    return () => clearTimeout(h);
  }, [load, user]);

  // Auto-refresh every 10 seconds so online/offline dots stay live.
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    const itv = setInterval(load, 10000);
    return () => clearInterval(itv);
  }, [load, user]);

  const versionOptions = useMemo(() => {
    const versions = new Set();
    (stats?.versions || []).forEach((v) => v.version && versions.add(v.version));
    return Array.from(versions).sort().reverse();
  }, [stats]);

  const handleBroadcastUpdate = useCallback(async () => {
    if (!window.confirm(
      "Broadcast a Force-Update command to EVERY online stick right now?\n\nEach online launcher will immediately poll /api/launcher/latest and download the newest APK if one is available — bypasses their normal 30-minute poll window."
    )) return;
    setBroadcasting(true);
    const tId = toast.loading("Broadcasting update signal to all online sticks…");
    try {
      const { data } = await api.post("/admin/fleet/broadcast-update");
      toast.success(`Queued force-update on ${data.queued} of ${data.eligible} online sticks`, { id: tId, duration: 6000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Broadcast failed", { id: tId });
    } finally {
      setBroadcasting(false);
    }
  }, []);


  const handleRemove = useCallback(async (screen) => {
    const label = screen.name || screen.device_id?.slice(0, 12) || "this screen";
    const owner = screen.owner_email ? ` owned by ${screen.owner_email}` : "";
    if (!window.confirm(`Remove "${label}"${owner}?\n\nThis unpairs the device, deletes its command history, and resyncs the owner's Stripe quantity. The device can re-pair with a new code afterwards.`)) {
      return;
    }
    setDeletingId(screen.id);
    try {
      await api.delete(`/admin/fleet/screens/${screen.id}`);
      setScreens((prev) => prev.filter((s) => s.id !== screen.id));
      toast.success(`Removed ${label}`);
      // Refresh stats in the background so the top pills stay accurate.
      api.get("/admin/fleet/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove screen");
    } finally {
      setDeletingId(null);
    }
  }, []);

  if (!user || user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-[1400px] text-slate-900" data-testid="admin-fleet-page">
        {/* ── Hero ────────────────────────────────────── */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3 flex items-center gap-2">
            <Shield className="h-3 w-3" /> Admin · Fleet
          </div>
          <h1 className="text-4xl md:text-5xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
            Every screen{" "}
            <span style={accentFont} className="text-sky-600">Nexso</span>{" "}
            is connected to.
          </h1>
          <p className="mt-3 text-sm text-slate-500">
            Cross-tenant view — click any screen to open the Remote Control dialog and troubleshoot without impersonating the owner.
          </p>
        </div>

        {/* ── Stats bar ────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="fleet-stats">
          <StatPill label="Total screens"  value={stats?.total ?? "—"}    tone="slate"    testid="stat-total" />
          <StatPill label="Online now"     value={stats?.online ?? "—"}   tone="emerald"  testid="stat-online" />
          <StatPill label="Paired"         value={stats?.paired ?? "—"}   tone="sky"      testid="stat-paired" />
          <StatPill label="Unpaired"       value={stats?.unpaired ?? "—"} tone="amber"    testid="stat-unpaired" />
        </div>

        {stats?.versions?.length > 0 && (
          <div className="mb-4 flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-500 uppercase tracking-widest">Launcher versions:</span>
            {stats.versions.map((v) => (
              <span key={v.version} className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 font-mono text-slate-700" data-testid={`version-${v.version}`}>
                v{v.version} · <span className="font-semibold">{v.count}</span>
              </span>
            ))}
          </div>
        )}

        {/* ── Filter bar ────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4" data-testid="fleet-filters">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email, screen name, IP, device model…"
              className="pl-10 bg-white border-slate-200 rounded-full h-11 text-sm"
              data-testid="fleet-search"
            />
          </div>
          <FilterChips
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { value: "all",      label: "All"       },
              { value: "online",   label: "Online"    },
              { value: "offline",  label: "Offline"   },
              { value: "unpaired", label: "Unpaired"  },
            ]}
            testid="fleet-status-chips"
          />
          {versionOptions.length > 0 && (
            <select
              value={versionFilter}
              onChange={(e) => setVersionFilter(e.target.value)}
              className="rounded-full bg-white border border-slate-200 text-sm px-4 h-11 focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="fleet-version-filter"
            >
              <option value="all">Any version</option>
              {versionOptions.map((v) => (
                <option key={v} value={v}>v{v}</option>
              ))}
            </select>
          )}
          <Button
            variant="outline"
            onClick={load}
            className="rounded-full h-11 bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100"
            data-testid="fleet-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button
            onClick={handleBroadcastUpdate}
            disabled={broadcasting}
            className="rounded-full h-11 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm disabled:opacity-50"
            title="Broadcast a force-update-check command to every online stick — bypasses their 30-min OTA poll window so a freshly-uploaded APK propagates in seconds instead of half an hour."
            data-testid="fleet-broadcast-update-btn"
          >
            <Rocket className={`h-4 w-4 mr-2 ${broadcasting ? "animate-pulse" : ""}`} />
            {broadcasting ? "Broadcasting…" : "Force update all sticks"}
          </Button>
        </div>

        {/* ── Fleet table ────────────────────────────────────── */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="fleet-table">
          {loading && screens.length === 0 ? (
            <div className="p-16 text-center text-slate-400 text-sm">Loading fleet…</div>
          ) : screens.length === 0 ? (
            <div className="p-16 text-center text-slate-400 text-sm">
              <Monitor className="h-8 w-8 mx-auto mb-3 text-slate-300" />
              No screens match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
                    <th className="text-left px-4 py-3">Screen</th>
                    <th className="text-left px-4 py-3">Owner</th>
                    <th className="text-left px-4 py-3">Device</th>
                    <th className="text-left px-4 py-3">Network</th>
                    <th className="text-left px-4 py-3">Version</th>
                    <th className="text-left px-4 py-3">Seen</th>
                    <th className="text-right px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {screens.map((s) => {
                    const di = s.device_info || {};
                    return (
                      <tr key={s.id || s.device_id} className="hover:bg-sky-50/40 transition-colors" data-testid={`fleet-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span
                              className={`inline-block w-2 h-2 rounded-full ${s.is_online ? "bg-emerald-500" : "bg-slate-300"}`}
                              title={s.is_online ? "Online" : "Offline"}
                            />
                            <div>
                              <div className="font-semibold text-slate-900">{s.name || "(unnamed)"}</div>
                              <div className="text-[10px] text-slate-400 font-mono">{s.device_id?.slice(0, 20)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {s.is_paired ? (
                            <div>
                              <div className="text-slate-900 text-xs font-medium">{s.owner_email}</div>
                              {s.owner_business && (
                                <div className="text-[10px] text-slate-400 flex items-center gap-1">
                                  <Building2 className="h-2.5 w-2.5" />
                                  {s.owner_business}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-400 italic text-xs">unpaired</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {(di.manufacturer || di.model) ? (
                            <div className="flex items-center gap-1.5 text-xs">
                              <Cpu className="h-3 w-3 text-slate-400" />
                              <span className="text-slate-700">{[di.manufacturer, di.model].filter(Boolean).join(" ")}</span>
                            </div>
                          ) : <span className="text-slate-400 italic text-xs">—</span>}
                          {di.os && (
                            <div className="text-[10px] text-slate-400">{di.os} {di.os_version || ""}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {di.local_ip ? (
                            <div className="text-xs">
                              <div className="flex items-center gap-1 text-slate-900 font-mono">
                                <Globe2 className="h-3 w-3 text-sky-600" />
                                {di.local_ip}
                              </div>
                              {di.wifi_ssid && (
                                <div className="flex items-center gap-1 text-[10px] text-slate-500">
                                  <Wifi className="h-2.5 w-2.5" />
                                  {di.wifi_ssid}
                                </div>
                              )}
                            </div>
                          ) : s.public_ip ? (
                            <div className="text-xs text-slate-500 font-mono">
                              WAN {s.public_ip}
                            </div>
                          ) : (
                            <span className="text-slate-300 italic text-xs">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {di.app_version ? (
                            <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-mono ${(di.app_version >= "1.4.3") ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                              v{di.app_version}
                            </span>
                          ) : <span className="text-slate-300 italic text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          {s.is_online ? (
                            <span className="text-emerald-600 text-xs font-medium flex items-center gap-1">
                              <Wifi className="h-3 w-3" />
                              online
                            </span>
                          ) : (
                            <span className="text-slate-400 text-xs flex items-center gap-1">
                              <WifiOff className="h-3 w-3" />
                              {s.last_seen_at ? formatSeen(s.last_seen_at) : "never"}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full bg-sky-50 border-sky-200 text-sky-700 hover:bg-sky-100"
                              onClick={() => setRemoteScreen(s)}
                              disabled={!s.is_paired}
                              data-testid={`fleet-remote-${s.id}`}
                            >
                              <Wrench className="h-3.5 w-3.5 mr-1" />
                              Remote
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full bg-rose-50 border-rose-200 text-rose-700 hover:bg-rose-100 disabled:opacity-50"
                              onClick={() => handleRemove(s)}
                              disabled={deletingId === s.id}
                              title="Remove screen from fleet"
                              data-testid={`fleet-remove-${s.id}`}
                            >
                              <Trash2 className={`h-3.5 w-3.5 ${deletingId === s.id ? "animate-pulse" : ""}`} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Shared Remote Control dialog — admin mode ─────────────────── */}
      <RemoteControlDialog
        screen={remoteScreen}
        onClose={() => setRemoteScreen(null)}
        adminMode={true}
      />
    </DashboardLayout>
  );
}

function StatPill({ label, value, tone, testid }) {
  const tones = {
    slate:   { bg: "#F1F5F9", border: "#E2E8F0", text: "#0F172A" },
    emerald: { bg: "#ECFDF5", border: "#A7F3D0", text: "#065F46" },
    sky:     { bg: "#F0F9FF", border: "#BAE6FD", text: "#0369A1" },
    amber:   { bg: "#FFFBEB", border: "#FDE68A", text: "#B45309" },
  }[tone] || {};
  return (
    <div className="rounded-2xl p-4 border" style={{ background: tones.bg, borderColor: tones.border }} data-testid={testid}>
      <div className="text-3xl font-semibold tracking-tight" style={{ ...headingFont, color: tones.text }}>{value}</div>
      <div className="text-[10px] uppercase tracking-[0.25em] mt-1 font-semibold" style={{ color: tones.text, opacity: 0.7 }}>{label}</div>
    </div>
  );
}

function FilterChips({ value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${value === o.value ? "bg-sky-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          data-testid={`fleet-status-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function formatSeen(iso) {
  if (!iso) return "never";
  const d = new Date(iso).getTime();
  if (!d) return "—";
  const s = Math.floor((Date.now() - d) / 1000);
  if (s < 60)   return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)   return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
